import { useMutation, useQuery, useQueryClient, type QueryClient } from '@tanstack/react-query';

import { api } from '@/lib/api';
import type {
  DayAvailability,
  SaveOverrideInput,
  SaveWeeklyScheduleInput,
  ScheduleOverrideView,
  SchedulingTeamMember,
  WeeklyScheduleView,
} from '@shared/schemas/availability';

/*
 * The queries behind /admin/scheduling — ROADMAP.md §7.1 and §7.2.
 *
 * **Every read here is keyed by its window as well as by the employee**, so
 * paging a month back is its own cache entry rather than a refetch that
 * overwrites the one on screen. The server refuses a window over 62 days, which
 * is the P8 rule applied to a calendar-shaped read: the visible range *is* the
 * limit.
 */

const ROOT = ['admin', 'scheduling'] as const;

function invalidate(client: QueryClient): Promise<void> {
  return client.invalidateQueries({ queryKey: ROOT });
}

/** The team list down the left. Active employees only. */
export function useSchedulingTeam() {
  return useQuery({
    queryKey: [...ROOT, 'team'],
    queryFn: () => api.get<{ data: SchedulingTeamMember[] }>('/api/admin/scheduling/team'),
  });
}

export function useWeeklySchedule(employeeId: string | null) {
  return useQuery({
    queryKey: [...ROOT, 'weekly', employeeId],
    queryFn: () =>
      api.get<WeeklyScheduleView>(`/api/admin/scheduling/employees/${employeeId ?? ''}/weekly`),
    enabled: employeeId !== null,
  });
}

export function useSaveWeeklySchedule(employeeId: string) {
  const client = useQueryClient();
  return useMutation({
    mutationFn: (input: SaveWeeklyScheduleInput) =>
      api.put<WeeklyScheduleView>(`/api/admin/scheduling/employees/${employeeId}/weekly`, input),
    // The team list carries the same totals in its left column, so it has to
    // move when the week does — otherwise the badge and the row disagree until
    // somebody reloads.
    onSuccess: () => invalidate(client),
  });
}

export function useOverrides(employeeId: string | null, from: string, to: string) {
  return useQuery({
    queryKey: [...ROOT, 'overrides', employeeId, from, to],
    queryFn: () =>
      api.get<{ data: ScheduleOverrideView[] }>(
        `/api/admin/scheduling/employees/${employeeId ?? ''}/overrides?from=${from}&to=${to}`,
      ),
    enabled: employeeId !== null,
    placeholderData: (previous) => previous,
  });
}

/**
 * The resolved answer for a window — override, else weekly, else unavailable.
 * This is what the Date Overrides calendar paints from, so the grid and the
 * assignment screen cannot disagree about who is working.
 */
export function useResolvedAvailability(employeeId: string | null, from: string, to: string) {
  return useQuery({
    queryKey: [...ROOT, 'availability', employeeId, from, to],
    queryFn: () =>
      api.get<{ data: DayAvailability[] }>(
        `/api/admin/scheduling/employees/${employeeId ?? ''}/availability?from=${from}&to=${to}`,
      ),
    enabled: employeeId !== null,
    placeholderData: (previous) => previous,
  });
}

export function useSaveOverride(employeeId: string) {
  const client = useQueryClient();
  return useMutation({
    mutationFn: (input: SaveOverrideInput) =>
      api.put<ScheduleOverrideView>(
        `/api/admin/scheduling/employees/${employeeId}/overrides`,
        input,
      ),
    onSuccess: () => invalidate(client),
  });
}

export function useDeleteOverride(employeeId: string) {
  const client = useQueryClient();
  return useMutation({
    mutationFn: (overrideId: string) =>
      api.delete(`/api/admin/scheduling/employees/${employeeId}/overrides/${overrideId}`),
    onSuccess: () => invalidate(client),
  });
}
